import React, { useState, useEffect, useCallback } from "react";
import Layout from "../../components/layout";
import { useDispatch, useSelector } from "react-redux";
import axios from "axios";
import { showLoading, hideLoading } from "../../redux/alertsSlice";
import DoctorForm from "../../components/DoctorForm";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "react-hot-toast";
import moment from "moment";

/* ── Design tokens ─────────────────────────────────────────── */
const EMERALD    = "#0d9488";
const TEAL       = "#14b8a6";
const TEAL_LIGHT = "#ccfbf1";
const SLATE      = "#0f172a";
const SLATE_MID  = "#334155";
const MUTED      = "#94a3b8";
const SURFACE    = "#f8fafc";
const WHITE      = "#ffffff";

/* ── Styles injected once ───────────────────────────────────── */
const injectStyles = () => {
  if (document.getElementById("doctor-profile-styles")) return;
  const el = document.createElement("style");
  el.id = "doctor-profile-styles";
  el.textContent = `
    @import url('https://fonts.googleapis.com/css2?family=Sora:wght@300;400;600;700;800&family=DM+Sans:ital,wght@0,300;0,400;0,500;1,300&display=swap');

    .dp-root { font-family: 'DM Sans', sans-serif; max-width: 860px; margin: 0 auto; }

    /* ── Profile hero card ── */
    .dp-hero-card {
      position: relative; overflow: hidden;
      background: linear-gradient(135deg, ${EMERALD} 0%, ${TEAL} 100%);
      border-radius: 20px; padding: 32px 36px;
      margin-bottom: 28px; color: ${WHITE};
    }
    .dp-hero-blob {
      position: absolute; top: -100px; right: -80px;
      width: 300px; height: 300px; border-radius: 50%;
      background: rgba(255,255,255,0.07); pointer-events: none;
    }
    .dp-hero-blob-2 {
      position: absolute; bottom: -60px; left: 35%;
      width: 180px; height: 180px; border-radius: 50%;
      background: rgba(255,255,255,0.05); pointer-events: none;
    }

    /* ── Avatar ── */
    .dp-avatar {
      width: 72px; height: 72px; border-radius: 50%;
      background: rgba(255,255,255,0.2);
      border: 3px solid rgba(255,255,255,0.4);
      display: flex; align-items: center; justify-content: center;
      font-family: 'Sora', sans-serif; font-weight: 700; font-size: 26px;
      color: ${WHITE}; flex-shrink: 0; margin-right: 20px;
      backdrop-filter: blur(6px);
    }
    .dp-hero-name {
      font-family: 'Sora', sans-serif; font-weight: 700;
      font-size: 22px; margin: 0 0 4px;
    }
    .dp-hero-meta {
      font-size: 13px; opacity: 0.8; margin: 0;
      display: flex; flex-wrap: wrap; gap: 16px; margin-top: 6px;
    }
    .dp-hero-meta-item { display: flex; align-items: center; gap: 5px; }
    .dp-hero-pill {
      background: rgba(255,255,255,0.18);
      border: 1px solid rgba(255,255,255,0.3);
      border-radius: 20px; padding: 3px 12px;
      font-size: 11px; font-weight: 600;
      display: inline-block; margin-top: 10px;
    }

    /* ── Eyebrow label ── */
    .dp-eyebrow {
      display: inline-block; font-family: 'Sora', sans-serif;
      font-size: 10px; font-weight: 700; letter-spacing: 2px;
      text-transform: uppercase; color: ${EMERALD};
      padding: 3px 10px; border-radius: 20px;
      background: ${TEAL_LIGHT}; margin-bottom: 14px;
    }

    /* ── Form card ── */
    .dp-form-card {
      background: ${WHITE};
      border: 1.5px solid #e2e8f0;
      border-radius: 18px;
      padding: 32px 36px;
      box-shadow: 0 2px 20px rgba(13,148,136,0.06);
    }

    /* ── Ant Design form overrides ── */
    .dp-form-card .ant-form-item-label > label {
      font-size: 12px; font-weight: 700;
      letter-spacing: 0.5px; text-transform: uppercase; color: ${SLATE_MID};
    }
    .dp-form-card .ant-input,
    .dp-form-card .ant-input-number,
    .dp-form-card .ant-select-selector,
    .dp-form-card .ant-picker {
      border-radius: 10px !important;
      border: 1.5px solid #e2e8f0 !important;
      font-family: 'DM Sans', sans-serif !important;
      font-size: 14px !important;
      transition: border-color 0.2s, box-shadow 0.2s !important;
    }
    .dp-form-card .ant-input:focus,
    .dp-form-card .ant-input-focused,
    .dp-form-card .ant-input-number-focused,
    .dp-form-card .ant-select-focused .ant-select-selector,
    .dp-form-card .ant-picker-focused {
      border-color: ${TEAL} !important;
      box-shadow: 0 0 0 3px rgba(20,184,166,0.12) !important;
    }
    .dp-form-card .ant-btn-primary {
      background: linear-gradient(135deg, ${EMERALD}, ${TEAL}) !important;
      border: none !important; border-radius: 10px !important;
      font-family: 'DM Sans', sans-serif; font-weight: 600;
      height: 42px; font-size: 14px;
      box-shadow: 0 4px 14px rgba(13,148,136,0.3) !important;
      transition: all 0.2s !important;
    }
    .dp-form-card .ant-btn-primary:hover {
      transform: translateY(-1px) !important;
      box-shadow: 0 6px 20px rgba(13,148,136,0.4) !important;
    }

    /* ── Divider strip ── */
    .dp-section-divider {
      display: flex; align-items: center; gap: 12px;
      margin: 24px 0 20px;
    }
    .dp-section-divider::before,
    .dp-section-divider::after {
      content: ''; flex: 1; height: 1px; background: #e2e8f0;
    }
    .dp-section-divider-label {
      font-size: 11px; font-weight: 700; letter-spacing: 1px;
      text-transform: uppercase; color: ${MUTED};
      white-space: nowrap;
    }

    /* ── Loading skeleton ── */
    .dp-skeleton {
      background: linear-gradient(90deg, #f0fdfa 25%, #ccfbf1 50%, #f0fdfa 75%);
      background-size: 200% 100%;
      animation: dpShimmer 1.4s infinite;
      border-radius: 10px;
    }
    @keyframes dpShimmer {
      0%   { background-position: 200% 0; }
      100% { background-position: -200% 0; }
    }

    /* ── Fade animations ── */
    @keyframes dpFadeUp {
      from { opacity: 0; transform: translateY(14px); }
      to   { opacity: 1; transform: translateY(0); }
    }
    .dp-fade-1 { animation: dpFadeUp 0.4s ease both 0.05s; }
    .dp-fade-2 { animation: dpFadeUp 0.4s ease both 0.15s; }

    /* ── Info chips in hero ── */
    .dp-chips { display: flex; flex-wrap: wrap; gap: 8px; margin-top: 14px; }
    .dp-chip {
      background: rgba(255,255,255,0.15);
      border: 1px solid rgba(255,255,255,0.25);
      border-radius: 20px; padding: 4px 14px;
      font-size: 12px; font-weight: 500;
    }
  `;
  document.head.appendChild(el);
};

/* ── Component ──────────────────────────────────────────────── */
function Profile() {
  const { user } = useSelector((state) => state.user);
  const params = useParams();
  const [doctor, setDoctor] = useState(null);
  const navigate = useNavigate();
  const dispatch = useDispatch();

  useEffect(() => { injectStyles(); }, []);

  const onFinish = async (values) => {
    try {
      dispatch(showLoading());
      const response = await axios.post(
        "/api/doctor/update-doctor-info-by-user-id",
        {
          ...values,
          userId: user._id,
          appointmentTime: [
            moment(values.appointmentTime[0]).format("HH:mm"),
            moment(values.appointmentTime[1]).format("HH:mm"),
          ],
        },
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        }
      );
      dispatch(hideLoading());
      if (response.data.success) {
        toast.success(response.data.message);
        navigate("/");
      } else {
        toast.error(response.data.message);
      }
    } catch (error) {
      dispatch(hideLoading());
      toast.error("Something went wrong");
    }
  };

  const getDoctorData = useCallback(async () => {
    try {
      dispatch(showLoading());
      const response = await axios.post(
        "/api/doctor/get-doctor-info-by-user-id",
        { userId: params.userId },
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        }
      );
      dispatch(hideLoading());
      if (response.data.success) {
        const doctorData = response.data.data;
        setDoctor({
          ...doctorData,
          appointmentDate: moment(doctorData.appointmentDate, "YYYY-MM-DD"),
        });
      }
    } catch (error) {
      dispatch(hideLoading());
    }
  }, [dispatch, params.userId]);

  useEffect(() => {
    getDoctorData();
  }, [getDoctorData]);

  /* Derive initials from doctor name */
  const fullName = doctor
    ? `${doctor.firstName || ""} ${doctor.lastName || ""}`.trim()
    : "";
  const avatarText = fullName
    .split(" ")
    .map((w) => w[0])
    .join("")
    .slice(0, 2)
    .toUpperCase() || "Dr";

  return (
    <Layout>
      <div className="dp-root">

        {/* ── Hero profile header ── */}
        {doctor ? (
          <div className="dp-hero-card dp-fade-1">
            <div className="dp-hero-blob" />
            <div className="dp-hero-blob-2" />

            {/* Avatar + name row */}
            <div style={{ display: "flex", alignItems: "flex-start", position: "relative", zIndex: 1 }}>
              <div className="dp-avatar">{avatarText}</div>
              <div>
                <h1 className="dp-hero-name">
                  Dr. {fullName || "Doctor"}
                </h1>
                <div className="dp-chips">
                  {doctor.specialization && (
                    <span className="dp-chip">{doctor.specialization}</span>
                  )}
                  {doctor.experience && (
                    <span className="dp-chip">{doctor.experience} yrs experience</span>
                  )}
                  {doctor.feePerConsultation && (
                    <span className="dp-chip">₦{doctor.feePerConsultation} / consult</span>
                  )}
                </div>
                <div className="dp-hero-meta" style={{ marginTop: 10 }}>
                  {doctor.email && (
                    <span className="dp-hero-meta-item">{doctor.email}</span>
                  )}
                  {doctor.phone && (
                    <span className="dp-hero-meta-item">{doctor.phone}</span>
                  )}
                  {doctor.address && (
                    <span className="dp-hero-meta-item">{doctor.address}</span>
                  )}
                </div>
              </div>
            </div>
          </div>
        ) : (
          /* Loading skeleton */
          <div className="dp-fade-1" style={{ marginBottom: 28 }}>
            <div
              className="dp-skeleton"
              style={{ height: 160, borderRadius: 20 }}
            />
          </div>
        )}

        {/* ── Form section ── */}
        {doctor && (
          <div className="dp-fade-2">
            <span className="dp-eyebrow">Edit profile</span>
            <div className="dp-form-card">
              <DoctorForm onFinish={onFinish} initivalValues={doctor} />
            </div>
          </div>
        )}

      </div>
    </Layout>
  );
}

export default Profile;